import { chipClasses, type ChipProps } from '@mui/material';
import { Theme } from '@mui/material/styles';

const COLORS = [
  'default',
  'primary',
  'success',
  'warning',
  'error',
  'purple',
  'violet',
  'rose',
] as const;

// NEW VARIANT
declare module '@mui/material/Chip' {
  interface ChipOwnProps {
    shape?: 'rounded' | 'square';
  }
  interface ChipPropsVariantOverrides {
    soft: true;
  }

  interface ChipPropsColorOverrides {
    purple: true;
    violet: true;
    rose: true;
  }

  interface ChipPropsSizeOverrides {
    xsmall: true;
    xxsmall: true;
  }
}

export default function Chip(theme: Theme) {
  const isLight = theme.palette.mode === 'light';

  const rootStyles = (ownerState: ChipProps) => {
    const roundedShape = ownerState.shape === 'rounded';

    const defaultColor = ownerState.color === 'default';

    const filledVariant = ownerState.variant === 'filled';

    const outlinedVariant = ownerState.variant === 'outlined';

    const softVariant = ownerState.variant === 'soft';

    const smallSize = ownerState.size === 'small';

    const xsmallSize = ownerState.size === 'xsmall';

    const xxsmallSize = ownerState.size === 'xxsmall';

    const mediumSize = ownerState.size === 'medium';

    const isClickable = ownerState.clickable || !!ownerState.onClick;

    const defaultStyle = {
      cursor: isClickable ? 'pointer' : 'default',
      [`& .${chipClasses.icon}`]: {
        margin: 0,
      },
      [`& > .${chipClasses.label}`]: {
        padding: 0,
      },
      [`& .${chipClasses.avatar}`]: {
        marginRight: theme.spacing(0.5),
        marginLeft: '-2px',
      },

      [`& > .${chipClasses.deleteIcon}`]: {
        margin: 0,
      },

      padding: theme.spacing(0.5),
      lineHeigh: 1,

      ...(roundedShape && {
        borderRadius: '999px',
      }),

      ...(!roundedShape && {
        borderRadius: theme.spacing(0.5),
      }),

      [`& > .${chipClasses.label} > .MuiChip-labelContent > p`]: {
        padding: theme.spacing(0, 0.5),
        // lineHeight: 1,
      },
    };

    const colorStyle = COLORS.map((color) => ({
      ...(ownerState.color === color && {
        [`& .${chipClasses.avatar}`]: {
          color: theme.palette[color][100],
          backgroundColor: theme.palette[color].dark,
        },

        border: `1px solid ${
          color === 'default'
            ? theme.palette.grey[300]
            : theme.palette[color][200]
        }`,
        color: theme.palette[color][600],

        [`& > .${chipClasses.label} > .MuiChip-labelContent > p`]: {
          color: theme.palette[color][600],
        },

        [`& > .${chipClasses.deleteIcon}`]: {
          color: theme.palette[color][600],
          margin: 0,
          marginInlineStart: 2,
          '&:hover': {
            opacity: 1,
            color: theme.palette[color].dark,
          },
        },

        // FILLED
        ...(filledVariant && {
          backgroundColor: theme.palette[color][50],

          ...(isClickable && {
            '&:hover': {
              backgroundColor:
                color === 'default'
                  ? theme.palette.grey[300]
                  : theme.palette[color][100],
            },
          }),
          ...(isClickable && {
            '&:focus': {
              backgroundColor:
                color === 'default'
                  ? theme.palette.grey[400]
                  : theme.palette[color][200],
              borderColor:
                color === 'default'
                  ? theme.palette.grey[400]
                  : theme.palette[color][200],
            },
          }),
        }),

        // OUTLINED
        ...(outlinedVariant && {
          backgroundColor: theme.palette.common.white,
          border: `solid 1px ${theme.palette[color][300]}`,
        }),

        // SOFT (same as filled but without border)
        ...(softVariant && {
          backgroundColor: theme.palette[color][50],
          border: 'none',

          ...(isClickable && {
            '&:hover': {
              backgroundColor:
                color === 'default'
                  ? theme.palette.grey[300]
                  : theme.palette[color][100],
            },
          }),
          ...(isClickable && {
            '&:focus': {
              backgroundColor:
                color === 'default'
                  ? theme.palette.grey[400]
                  : theme.palette[color][200],
            },
          }),
        }),
      }),
    }));

    const size = {
      ...(xxsmallSize && {
        height: 12,
        fontSize: theme.typography.body5.fontSize,
        fontWeight: theme.typography.fontWeightRegular,
        letterSpacing: theme.typography.body5.letterSpacing,
        lineHeight: 1,
        [`& > .${chipClasses.icon}`]: {
          height: 10,
          width: 10,
          ' > svg': {
            height: 10,
            width: 10,
            margin: 0,
          },
        },
        [`& > .${chipClasses.label} > .MuiChip-labelContent > p`]: {
          fontSize: theme.typography.pxToRem(8),
          lineHeight: theme.typography.pxToRem(12),
        },
      }),
      ...(xsmallSize && {
        height: 16,
        fontSize: theme.typography.body5.fontSize,
        fontWeight: theme.typography.fontWeightRegular,
        letterSpacing: theme.typography.body5.letterSpacing,
        lineHeight: 1,
        [`& > .${chipClasses.icon}`]: {
          height: 12,
          width: 12,
          ' > svg': {
            height: 12,
            width: 12,
            margin: 0,
          },
        },
        [`& > .${chipClasses.label} > .MuiChip-labelContent > p`]: {
          fontSize: theme.typography.pxToRem(8),
          lineHeight: theme.typography.pxToRem(12),
        },
        [`& > .${chipClasses.avatar}`]: {
          height: 12,
          width: 12,
          fontSize: theme.typography.body5.fontSize,
        },
      }),

      [`& > .${chipClasses.label} > .MuiChip-labelContent > .MuiChip-endIcon`]:
        {
          height: 12,
          width: 12,
          minWidth: 12,
          ' > svg': {
            height: 12,
            width: 12,
            minWidth: 12,
            margin: 0,
          },
        },
      ...(smallSize && {
        height: 24,
        fontSize: theme.typography.body4.fontSize,
        fontWeight: theme.typography.fontWeightRegular,
        letterSpacing: theme.typography.body4.letterSpacing,
        lineHeight: theme.typography.body4.lineHeight,
        [`& > .${chipClasses.icon}`]: {
          height: 16,
          width: 16,
          minWidth: 16,
          ' > svg': {
            height: 16,
            width: 16,
            minWidth: 16,
            margin: 0,
          },
        },
        [`& > .${chipClasses.label} > .MuiChip-labelContent > p`]: {
          fontSize: theme.typography.body4.fontSize,
          lineHeight: theme.typography.body4.lineHeight,
        },
      }),
      ...(mediumSize && {
        height: 32,
        fontSize: theme.typography.body4.fontSize,
        fontWeight: theme.typography.fontWeightRegular,
        letterSpacing: theme.typography.body4.letterSpacing,
        lineHeight: theme.typography.body4.lineHeight,
        [`& > .${chipClasses.icon}`]: {
          height: 16,
          width: 16,
          minWidth: 16,
          ' > svg': {
            height: 16,
            width: 16,
            minWidth: 16,
            margin: 0,
          },
        },
        [`& > .${chipClasses.label} > .MuiChip-labelContent > p`]: {
          fontSize: theme.typography.body4.fontSize,
          lineHeight: theme.typography.body4.lineHeight,
        },
      }),
    };

    const disabledState = {
      [`&.${chipClasses.disabled}`]: {
        opacity: 1,
        color: theme.palette.action.disabled,

        [`& .${chipClasses.icon}`]: {
          color: theme.palette.action.disabled,
        },
        [`& .${chipClasses.avatar}`]: {
          color: theme.palette.action.disabled,
          backgroundColor: theme.palette.action.disabledBackground,
        },
        backgroundColor: theme.palette.grey[100],
        borderColor: theme.palette.grey[100],
        [`& > .${chipClasses.icon}`]: {
          color: theme.palette.grey[400],
        },
        [`& > .${chipClasses.deleteIcon}`]: {
          color: theme.palette.grey[400],
        },
        [`& > .${chipClasses.label} > .MuiChip-labelContent > .MuiChip-endIcon`]:
          { color: theme.palette.grey[400] },
        [`& > .${chipClasses.label} > .MuiChip-labelContent > p`]: {
          color: theme.palette.grey[400],
        },
      },
    };

    return [defaultStyle, size, ...colorStyle, disabledState];
  };

  return {
    MuiChip: {
      defaultProps: {
        color: 'primary',
        component: 'button',
      },

      styleOverrides: {
        root: ({ ownerState }: { ownerState: ChipProps }) =>
          rootStyles(ownerState),
      },
    },
  };
}
